import Debug from "./debugging.js"

/**
 * @typedef {number} NsSinceEpoch
 *   Numeric timestamp in nanoseconds since Unix epoch.
 */

/**
 * @typedef {number} DiagEventCode
 *   Numeric identifier for a diagnostic event.
 */

/**
 * @typedef {number | string} MetricValue
 *   A reported metric value. May be numeric or textual.
 */

/**
 * @typedef {Object.<string, MetricValue>} MetricMap
 *   A map of reported metric values, keyed by name.
 */

/**
 * @callback DecChecker
 *   Checks whether any of a set of diagnostic events are active.
 *
 * @param {DiagEventCode | Iterable<DiagEventCode>} codes
 *   Diagnostic event codes to check.
 *
 * @returns {boolean}
 *   True if the event is active.
 */

/**
 * @callback MetricGetter
 *   Retrieves the current value of a reported metric.
 *
 * @param {string} name
 *   Name of the metric to retrieve.
 *
 * @returns {MetricValue | undefined}
 *   The metric value, or undefined if not yet reported.
 */

/**
 * @typedef {Object} TestContext
 *
 * @property {DecChecker} dec
 *   Function that checks whether a diagnostic event is active.
 *
 * @property {MetricGetter} metric
 *   Function that retrieves a metric value by name.
 *
 * @property {NsSinceEpoch} now
 *   The timestamp of the start of the current test execution.
 */

/**
 * @callback TestPredicate
 *   Evaluates whether the diagnostic test should trigger an alert in the HMI.
 *
 * @param {TestContext} ctx
 *   Context object containing helper functions and the current timestamp.
 *
 * @returns {boolean}
 *   True if the HMI should show the test's alert message.
 */

/**
 * @typedef {Object} DiagTest
 *
 * @property {string} name
 *   The unique test name.
 *
 * @property {string} message
 *   The alert message shown in the HMI alerts when the test fails.
 *
 * @property {string} [recovery]
 *   Optional recovery action message shown in the HMI alert if the test fails.
 *
 * @property {TestPredicate} predicate
 *   The evaluation function used to determine whether the configured alert
 *   should be shown in the HMI or not.
 *
 * @property {NsSinceEpoch} triggered
 *   Mutable state. The timestamp when the test last failed. It is reset to
 *   zero when the test passes.
 */

/**
 * @enum {number}
 */
export const TestStatus = Object.freeze({
  PASSED: 0, // Test predicate returned false
  FAILED: 1, // Test predicate returned true
  ERROR: 2,  // Test predicate threw exception or is missing metrics
});

/**
 * @typedef {Object} TestResult
 *   The result of a diagnostic test, with any missing metric names.
 *
 * @property {TestStatus} status
 *   Outcome of the test evaluation.
 *
 * @property {string[]} missingMetrics
 *   List of unrecognized metric names requested by the predicate.
 *   Can only be non-empty when the status is `ERROR`.
 */

/*
 * Creates a diagnostics test Object.
 *
 * The `predicate` function receives two helpers:
 *
 *  • dec(codes: DiagEventCode | DiagEventCode[]) => boolean
 *      Returns true if the any of the diagnostic events are active.
 *
 *  • metric(name: string) => MetricValue | undefined
 *      Retreives a reported metric by name, or undefined if not available.
 *
 * @note
 *   If the predicate requests metrics that are not yet available its result
 *   is ignored. The predicate does not need to guard against undefined values.
 *
 * @example
 *   tests += createDiagnosticTest({
 *     name: "foo",
 *     message: "Something happened!",
 *     recovery: "Restart the vehicle!",
 *     predicate: (dec, metric) => dec(123) || metric("foo") < 0.9
 *   });
 *
 * @param {string} name
 *   The name of the test.
 *
 * @param {string} message
 *   The alert message to display in the HMI when the test fails.
 *
 * @param {string} [recovery]
 *   Optional recovery action message to display when the test fails.
 *
 * @param {TestPredicate} predicate
 *   Function that evaluates the test condition using the provided helpers.
 *
 * @returns {{
 *   name: string,
 *   message: string,
 *   recovery: string,
 *   predicate: TestPredicate,
 *   triggered: NsSinceEpoch
 * }}
 *   A fully constructed diagnostics test object.
 */
export function createTest({name, message, recovery = "Restart the vehicle", predicate}) {

  if (!name) throw new Error("Invalid diagnostic test! Name property is missing or empty.");
  if (!message) throw new Error("Invalid diagnostic test! Message property is missing or empty.");
  if (!predicate) throw new Error("Invalid diagnostic test! Predicate callback is missing.");

  return {
    name,
    message,
    recovery,
    predicate,
    triggered: 0,
  };
}

/**
 * Executes a diagnostic test using the current diagnostic events and metrics.
 *
 * If the predicate requests metrics that are not yet reported, the test result
 * is treated as an ERROR.
 *
 * @param {DiagTest} test
 *   The configured diagnostics test to execute.
 *
 * @param {Set<DiagEventCode>} diagnostics
 *   A set of active diagnostic event codes.
 *
 * @param {MetricMap} metrics
 *   Map of reported metric values keyed by name.
 *
 * @param {NsSinceEpoch} timestamp
 *   The shared test execution timestamp.
 *
 * @returns {TestResult}
 *   The result of executing the test.
 */
export function executeTest(test, diagnostics, metrics, timestamp) {

  const toIntSet = (values) => {
    if (values instanceof Set) return values;
    if (typeof values === "number") return new Set([values]);
    return new Set(values);
  };

  const decChecker = codes => {
    for (const code of toIntSet(codes)) {
      if (diagnostics.has(code)) return true;
    }
    return false;
  };

  const missingMetrics = [];
  const metricGetter = name => {
    const value = metrics[name];
    if (value === undefined) missingMetrics.push(name);
    return value;
  };

  const context = {
    dec: decChecker,
    metric: metricGetter,
    now: timestamp
  };

  let status = TestStatus.ERROR;

  try {
    const triggered = test.predicate(context);
    status = triggered ? TestStatus.FAILED : TestStatus.PASSED;
  } catch (error) {
    console.error(`Diagnostic test ${test.name} threw error: ${error.message}`);
    status = TestStatus.ERROR;
  }

  if (missingMetrics.length !== 0) {
    status = TestStatus.ERROR;
  }

  return { status, missingMetrics };
}

/**
 * Updates the mutable state of a diagnostics test depending on its result.
 *
 * @param {DiagTest} test
 *   The test object to modify.
 *
 * @param {TestResult} result
 *   The test's execution result.
 *
 * @param {NsSinceEpoch} timestamp
 *   The timestamp used during the test execution.
 */
export function updateTestState(test, result, timestamp) {
  if (result.status === TestStatus.PASSED) test.triggered = 0;
  if (result.status === TestStatus.FAILED) test.triggered = timestamp;
}


/**
 * Runs a list of diagnostics tests, returning handles to any triggered tests
 * along with any metric missing metric names.
 *
 * @param {DiagTest[]} tests
 *   The list of diagnostics tests to run.
 *
 * @param {Set<DiagEventCode>} diagnostics
 *   Set of active diagnostic event codes.
 *
 * @param {MetricMap} metrics
 *   Map of reported metric values keyed by name.
 *
 * @param {NsSinceEpoch} timestamp
 *   The timestamp used during the test execution.
 *
 * @returns {Set<string>}
 *   Any metric names of requested metrics missing from the metrics map.
 */
export function runTests(tests, diagnostics, metrics, timestamp) {

  const missingMetrics = new Set();

  for (const test of tests) {
    const result = executeTest(test, diagnostics, metrics, timestamp);

    if (result.status !== TestStatus.ERROR) {
      updateTestState(test, result, timestamp);
    }

    for (const metric of result.missingMetrics) {
      missingMetrics.add(metric);
    }
  }

  return missingMetrics;
}

/**
 * Checks if a previously created alert message comes from a diagnostics test.
 *
 * @param {Object} message
 *   The alert message object to evaluate.
 *
 * @returns {boolean}
 *   True if the name attribute starts with "diag_test_".
 */
export function isDiagnosticsAlert(message) {
  return message.name.startsWith("diag_test_");
}

/**
 * Returns the alert message name associated with a diagnostics test.
 */
export function diagnosticsAlertName(test) {
  return `diag_test_${test.name}`;
}

/**
 * Stubs an insamla_utils.MonitorStatus message for an AlertItem.
 * The alert name will be the test name prefixed with 'diag_test_'.
 *
 * @param {Object} test
 *   The failed test that the message should warn about.
 *
 * @param {NsSinceEpoch} timestamp
 *   The timestamp used for the test execution.
 *
 * @returns {{
 *   name: string,
 *   status: boolean,
 *   messages: string[],
 *   data: {},
 *   timestamp: number,
 * }}
 *   An object matching the interface of an insamla_utils.MonitorStatus message.
 */
export function createDiagnosticsAlert(test, timestamp) {

  const messages = [test.message];
  if (test.recovery) messages.push(`Recovery action: ${test.recovery}`);

  return {
    name: diagnosticsAlertName(test),
    status: false,
    messages,
    data: {},
    timestamp,
  };
}

/**
 * Adds newly triggered diagnostics alerts and removes recovered ones.
 */
export function updateDiagnosticsAlerts(messages, newlyTriggered, newlyRecovered, timestamp) {
  return [
    ...messages.filter(message =>
      !isDiagnosticsAlert(message)
      || !newlyRecovered.some(test => diagnosticsAlertName(test) === message.name)
    ),
    ...newlyTriggered.map(test => createDiagnosticsAlert(test, timestamp)),
  ];
}

/**
 * Compresses the PTPD timesync statuses polled from ats-adpt-rest into metric
 * values for the diagnostic tests.
 *
 * @param {{name:string, state:string, isActive:boolean, reportsTime:boolean}[]} statuses
 *
 * @returns {Record<string, boolean>}
 */
export function compilePtpdTimesyncMetrics(statuses) {

  const badSync = {
    pcuA : null,
    lidars: null,
  };

  for (const {name, state, isActive, reportsTime} of statuses) {

    const isBad = !isActive || !reportsTime || state !== "in-sync";

    if (name === "pcu-a") {
       badSync.pcuA ||= isBad;
    }
    else if (name.startsWith("Lidar")) {
      badSync.lidars ||= isBad;
    }
  }

  const result = {};

  if (badSync.pcuA !== null) {
    result.bad_ptpd_pcu_a = badSync.pcuA;
  }

  if (badSync.lidars !== null) {
    result.bad_ptpd_lidars = badSync.lidars;
  }

  return result;
}

/**
 * Helper function for generating ranges of DECs.
 */
function* range(start, end, step = 1) {
  for (let i = start; i <= end; i += step) yield i;
};

/**
 * Creates the diagnostic tests for the default DaCK UI.
 *
 * @returns {DiagTest[]}
 *   List of configured diagnostics tests.
 */
export function createDiagnosticTests() {

  let tests = [];

  tests.push(createTest({
    name: "high_cpu_percent",
    message: "High CPU load detected",
    recovery: "Restart the vehicle if persistent",
    predicate: ({metric}) =>
      metric("cpu_percent_pcu_a") > 80 ||
      metric("cpu_percent_pcu_b") > 80
  }));

  tests.push(createTest({
    name: "high_ram_percent",
    message: "High RAM load detected",
    recovery: "Restart the vehicle if persistent",
    predicate: ({metric}) =>
      metric("ram_percent_pcu_a") > 80 ||
      metric("ram_percent_pcu_b") > 80
  }));

  tests.push(createTest({
    name: "bad_ptpd_pcu_a",
    message: "The main computer encounters timesync issues.",
    recovery: "Restart the vehicle if persistent",
    predicate: ({metric}) => metric("bad_ptpd_pcu_a")
  }));

  return tests;
}

export function createSensorTests({lidars, radars}) {

  //
  // Compile the sensor related DECs and process names
  //

  let sensorProcesses = [];
  let lidarDECs = new Set();
  let radarDECs = new Set();

  for (let i = 0; i < lidars.length; ++i) {

    sensorProcesses.push("ats-adpt-osdc-" + lidars[i].referenceFrame);
    for (const base of range(7_000, 7_018)) {
      lidarDECs.add(base + i * 100);
    }

    sensorProcesses.push("ats-adpt-osdi-" + lidars[i].referenceFrame);
    lidarDECs.add(70_000 + i * 100);
  }

  // todo: remove after testing in a vehicle. Is this needed or not in vehicles?
  if (Boolean(process.env.REACT_APP_IGNORE_EXPECTED_OSDI_DECS ?? false)) {
    /* TODO(abezqk, 2026-06-10):
     *   OSDI seems to always report DECs 70300, 70400 and 70700; missing IMU
     *   input data from Lidar 3, 4 and 7. We suppress those for now.
     *   But they should be enabled as soon as that is fixed.
     */
    lidarDECs.delete(70_300);
    lidarDECs.delete(70_400);
    lidarDECs.delete(70_700);
    console.warn("Suppressing DECs 70300, 70400 and 70700, since " +
                 "they are current expected to be set constantly.");
  }

  //
  // Debug logging
  //

  Debug.logGroup("Alert triggering sensor DECs compiled", {lidarDECs, radarDECs});

  //
  // Create the tests
  //

  let tests = [];

  tests.push(createTest({
    name: "lidar_problems",
    message: "Detected problems with one or more LiDAR sensors.",
    predicate: ({dec, metric}) => dec(lidarDECs) || metric("bad_ptpd_lidars"),
  }));

  tests.push(createTest({
    name: "sensor_processor_unresposive",
    message: "One or more sensor processor became unresponsive!",
    predicate: ({metric}) => sensorProcesses.some(proc => metric("process_status_" + proc) !== "OK"),
  }));

  return tests;
}
